<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('service.services') }}</span>
        <h3 class="page-title">{{ $t('service.group.servicesGroupsList') }}</h3>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button theme="success" @click.prevent="$router.push('/zdobienia/grupy/nowa')">
          <i class="material-icons">add</i>
          {{ $t('service.group.addNewGroup') }}
        </d-button>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="services-groups-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('service.group.enterServiceGroupNameOrCode')"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0" style="width: 80px;">{{ $t('image.photo') }}</th>
            <th scope="col" class="border-0">{{ $t('service.group.groupName') }}</th>
            <th scope="col" class="border-0">{{ $t('service.group.groupCode') }}</th>
            <th scope="col" class="border-0">{{ $t('service.group.assortmentGroup') }}</th>
            <th scope="col" class="border-0">{{ $t('service.group.numberOfVariants') }}</th>
            <th scope="col" class="border-0">{{ $t('service.group.numberOfServices') }}</th>
            <th scope="col" class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(group, index) of groups"
            :key="'group-' + index">
            <td class="align-middle">
              <img
                v-if="group.image_url"
                :src="group.image_url"
                alt=""
                class="group-image" />
              <span
                v-else
                class="group-empty-image">
                <i class="material-icons">camera_alt</i>
              </span>
            </td>
            <td class="align-middle">
              <d-badge
                v-if="group.is_package"
                theme="success">
                {{ $t('service.isPackage') }}
              </d-badge>
              <br v-if="group.is_package">
              {{ group.name }}<br>
              <small>{{ $t('service.embroidery.type') }}:</small>
              <small
                v-if="group.embroidery_type_name"
                class="text-primary">
                {{ group.embroidery_type_name }}
              </small>
              <small
                v-else
                class="text-primary">
                &mdash;
              </small>
            </td>
            <td class="align-middle">
              {{ group.code }}
            </td>
            <td class="align-middle">
              {{ group.assortment_group }}
            </td>
            <td class="align-middle">
              <template v-if="group.services">{{ group.services.length }}</template>
              <template v-else>0</template>
            </td>
            <td class="align-middle">
              <template v-if="group.package_services">{{ group.package_services.length }}</template>
              <template v-else>0</template>
            </td>
            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/zdobienia/grupy/edycja/' + group.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeGroup(group.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="groups.length === 0">
            <td
              colspan="6"
              class="text-center">
              {{ $t('service.group.noServicesGroupsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'services-groups',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  data() {
    return {
      groups: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      filters: {
        search: ''
      }
    };
  },
  mounted () {
    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/services/groups/items?page=' + this.currentPage,
        listField: 'groups',
        params: {
          with: ['services', 'packageServices'],
          where: {
            search: this.filters.search
          }
        }
      });
    },
    clearFilters () {
      this.filters = {
        search: ''
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['services-groups-filters-form'], this.debouncedLoadFilteredData);
    },
    removeGroup (groupID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/services/groups/delete/',
        id: groupID,
        successTitle: this.$t('service.group.removeServiceGroupSuccessTitle'),
        successText: this.$t('service.group.removeServiceGroupSuccessText'),
        confirmTitle: this.$t('service.group.removeServiceGroupConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('service.group.removeServiceGroupErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.group {
  &-image {
    height: auto;
    max-height: 80px;
    max-width: 80px;
    width: auto;
  }

  &-empty-image {
    align-items: center;
    background: #e5e5e5;
    color: #fff;
    display: flex;
    font-size: 48px;
    height: 80px;
    justify-content: center;
    width: 80px;
  }
}
</style>
